import { OrderStatus, OrderType } from "@/api/payments";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useListContext } from "react-admin";
import { formatOrderStatus, formatOrderType } from "../../utils";

type Props = {
  onModalClose: () => void;
};

const initialFormState = {
  statuses: [] as OrderStatus[],
  types: [] as OrderType[],
};

type FormData = typeof initialFormState;

const STATUS_OPTIONS = Object.values(OrderStatus);
const TYPE_OPTIONS = Object.values(OrderType);

const PaymentsFilter: FC<Props> = ({ onModalClose }) => {
  const { filterValues, setFilters } = useListContext();
  const [formData, setFormData] = useState<FormData>({
    statuses: filterValues.statuses ?? [],
    types: filterValues.types ?? [],
  });

  const toggle = <K extends keyof FormData>(
    field: K,
    value: FormData[K][number],
  ) => {
    const current = formData[field] as string[];
    const next = current.includes(value as string)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setFormData({ ...formData, [field]: next });
  };

  const onSubmit = () => {
    setFilters({
      ...filterValues,
      statuses: formData.statuses.length ? formData.statuses : undefined,
      types: formData.types.length ? formData.types : undefined,
    });
    onModalClose();
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Status
        </Typography>
        {STATUS_OPTIONS.map((status) => (
          <FormControlLabel
            key={status}
            label={formatOrderStatus(status)}
            labelPlacement="start"
            sx={{ ml: 0, justifyContent: "space-between" }}
            control={
              <Checkbox
                checked={formData.statuses.includes(status)}
                onChange={() => toggle("statuses", status)}
              />
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Type
        </Typography>
        {TYPE_OPTIONS.map((type) => (
          <FormControlLabel
            key={type}
            label={formatOrderType(type)}
            labelPlacement="start"
            sx={{ ml: 0, justifyContent: "space-between" }}
            control={
              <Checkbox
                checked={formData.types.includes(type)}
                onChange={() => toggle("types", type)}
              />
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => setFormData(initialFormState)}
        >
          Reset
        </Button>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onModalClose}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { PaymentsFilter };
